import {
  Card,
  CardHeader,
  makeStyles,
  Text,
  Caption1,
  CardFooter,
  Badge,
  Checkbox,
  Button,
  Spinner,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from '@fluentui/react-components';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { DeveloperPortalRootContext, useCommand, useSelector } from '@tdp/core';

import { PreValidationStatus } from '../abstractions/EAppValidation';
import { appValidationSlice } from '../redux/slice';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: '1.5rem'
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  button: {
    display: 'flex',
    gap: '6px'
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  }
})

interface IAppValidationNewProps {
  img: string;
  shortName: string;
  developer: string;
  shortDescription: string;
  manifestVersion: string;
  appVersion: string;
  onValidationCreated: () => void;
};

export const AppValidationNew: React.FC<IAppValidationNewProps> = (
  {
    img,
    shortName,
    developer,
    shortDescription,
    manifestVersion,
    appVersion,
    onValidationCreated
  }
) => {
  const classes = useStyles();

  const { appId } = useParams();
  const navigate = useNavigate();
  const createNewValidation = useCommand(s => s.appValidationCommands.createNewValidation);
  const resetNewValidationId = useCommand(s => s.appValidationCommands.resetNewValidationId);
  const preValidateAppDefinition = useCommand(s => s.appValidationCommands.preValidateAppDefinition);

  const newValidationId = useSelector(s => appValidationSlice.selectors.newValidationId(s))
  const preValidation = useSelector(s => appValidationSlice.selectors.preValidation(s))
  const resetPreValidation = useCommand(s => s.appValidationCommands.resetPreValidation);


  const [isManifestChecked, setIsManifestChecked] = React.useState(false);
  const [isSideloadChecked, setisSideloadChecked] = React.useState(false);
  const [isValidationRunChecked, setIsValidationRunChecked] = React.useState(false);
  const [isNewValidationStarted, setIsNewValidationStarted] = React.useState(false);
  const { services: { logger } } = React.useContext(DeveloperPortalRootContext);
  const isDisabled = isNewValidationStarted ||
    !(preValidation?.status === PreValidationStatus.Accepted) ||
    !(isManifestChecked && isSideloadChecked && isValidationRunChecked);

  const ManifestUtilisation = () => <FormattedMessage
    defaultMessage="Manifest utilization: Your app's manifest will be used for the purpose of validation. Ensure that it is up-to-date and accurate, as it will be essential for successful validation."
    description="Checkbox for manifest utilisation acknowledgement"
  />

  const AppSideloading = () => <FormattedMessage
    defaultMessage="App sideloading: For the validation process, your app will be sideloaded into a designated testing tenant temporarily. The app will be deleted from the tenant once validation is complete."
    description="Checkbox for app sideloading acknowledgement"
  />

  const ValidationRun = () => <FormattedMessage
    defaultMessage="Validation run limitation: Please note that only one validation run can be performed per app at a time. Ensure that you are not submitting multiple requests for the same app simultaneously."
    description="Checkbox for validation limitation acknowledgement"
  />

  React.useEffect(() => {
    if (appId) {
      preValidateAppDefinition({ appId })
    }
    return () => {
      resetPreValidation();
    }
  }, [appId]);


  React.useEffect(() => {
    if (newValidationId === "FAILED") {
      setIsNewValidationStarted(false);
      resetNewValidationId();
      return;
    }
    if (newValidationId && newValidationId !== "FAILED") {
      navigate(newValidationId)
    }
  }, [newValidationId])

  const onStartValidation = () => {
    logger.logUserBi({
      telemetryMarker: 'app-validation-action-new-start-validation',
      message: `User started a new validation for app`,
      appId,
    });
    setIsNewValidationStarted(true);
    if (appId) {
      createNewValidation({
        appDefinitionId: appId,
        appEnvironmentId: null
      })
      return;
    }
  }

  const PreValidationFailed = () => {
    if (preValidation?.status === PreValidationStatus.Rejected) {
      return (
        <div className={classes.title}>
          <Accordion collapsible defaultOpenItems={['pre-validation-failed']}>
            <AccordionItem value="pre-validation-failed">
              <AccordionHeader>
                <MessageBar intent='warning'>
                  <MessageBarBody>
                    <MessageBarTitle style={{ color: tokens.colorNeutralForeground1, }}>
                      <FormattedMessage
                        defaultMessage={'Please resolve the following errors before starting validation'}
                        description={'Title for pre-validation failure message'}
                      />
                    </MessageBarTitle>
                  </MessageBarBody>
                </MessageBar>
              </AccordionHeader>
              <AccordionPanel>
                <ul style={{ color: tokens.colorNeutralForeground1 }}>
                  {preValidation.errors?.map((error, index) => (
                    <li key={index}>
                      <Text weight="semibold">
                        {error.content || error.title || error.id || index.toString()}
                      </Text>
                    </li>
                  ))}
                </ul>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      );
    }
    return (<></>);
  }


  if (!preValidation?.status) {
    return (
      <div className={classes.spinner}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Card style={{ padding: '16px', maxWidth: '420px' }}>
        <CardHeader
          image={<img src={img} alt={shortName} width={42} height={42} style={{ borderRadius: '6px' }} />}
          header={<Text weight="semibold">{shortName}</Text>}
          description={<Caption1>{developer}</Caption1>}
        />
        {shortDescription}
        <CardFooter style={{ marginTop: '6px' }}>
          <Badge appearance="tint">
            <FormattedMessage
              defaultMessage={`Manifest version: ${manifestVersion}`}
              description={`Manifest version`}
            />
          </Badge>
          <Badge appearance="tint">
            <FormattedMessage
              defaultMessage={`App version: ${appVersion}`}
              description={`App version`}
            />
          </Badge>
        </CardFooter>
      </Card>
      <PreValidationFailed />
      <div className={classes.title}>
        <Text weight="semibold" style={{ color: tokens.colorNeutralForeground1 }}>
          <FormattedMessage
            defaultMessage={'Validation acknowledgement'}
            description={'Title for validation acknowledgement'}
          />
        </Text>
        <Caption1 style={{ color: tokens.colorNeutralForeground1 }}>
          <FormattedMessage
            defaultMessage={"To ensure a smooth and efficient validation process for your app, please read and acknowledge the following steps"}
            description={'Description for validation acknowledgement'}
          />
        </Caption1>
        <Checkbox
          checked={isManifestChecked}
          onChange={(_, data) => { setIsManifestChecked(!!data.checked); }}
          disabled={preValidation.status === PreValidationStatus.Rejected}
          label={<ManifestUtilisation />}
        />
        <Checkbox
          checked={isSideloadChecked}
          onChange={(_, data) => { setisSideloadChecked(!!data.checked); }}
          disabled={preValidation.status === PreValidationStatus.Rejected}
          label={<AppSideloading />}
        />
        <Checkbox
          checked={isValidationRunChecked}
          onChange={(_, data) => { setIsValidationRunChecked(!!data.checked); }}
          disabled={preValidation.status === PreValidationStatus.Rejected}
          label={<ValidationRun />}
        />
      </div>
      <div className={classes.button}>
        <Button
          appearance='primary'
          disabled={isDisabled}
          onClick={onStartValidation}
        >
          <FormattedMessage
            defaultMessage="Start validation"
            description={'Button to start validation'}
          />
        </Button>
        {isNewValidationStarted && <Spinner />}
      </div>
    </div >
  );
}